import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { SendIcon, MailIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { ChatMessage, TypingIndicator } from './ChatMessage'
import { QuickReplies } from './QuickReplies'
import { ThinkingSteps } from './ThinkingSteps'
import type { ThinkingStep } from './ThinkingSteps'
import { PlanView } from './PlanView'
import { ToolCallIndicator } from './ToolCallIndicator'
import { EvidencePanel } from './EvidencePanel'
import { TicketCard } from './TicketCard'
import { InteractionCard } from './InteractionCard'
import { streamSupportMessage, fetchChatHistory } from '@/lib/supportApi'
import type { ChatMessage as ChatMessageType, RelatedArticle, PlanStep, ToolCallEvent, EvidenceItem, TicketInfo, InteractionEvent } from '@/lib/supportApi'
import { ExternalLink } from 'lucide-react'

function newSessionId(): string {
  return crypto.randomUUID()
}

const WELCOME_MESSAGE: ChatMessageType = {
  role: 'assistant',
  content:
    'Bem-vindo à Central de ajuda! 👋\n\nPara te ajudar, preciso identificar sua conta. Qual é o seu **e-mail de cadastro**?',
}

const INITIAL_SUGGESTIONS = [
  'Preciso de ajuda com meu curso',
  'Informações sobre minha compra',
  'Falar com o suporte',
]

interface Message {
  role: 'user' | 'assistant'
  content: string
}

export function SupportChat() {
  const sessionIdRef = useRef(newSessionId())
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE])
  const [suggestions, setSuggestions] = useState<string[]>(INITIAL_SUGGESTIONS)
  const [relatedArticles, setRelatedArticles] = useState<RelatedArticle[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [streamingText, setStreamingText] = useState<string | null>(null)
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([])
  const [thinkingCollapsed, setThinkingCollapsed] = useState(false)
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [requiresEmail, setRequiresEmail] = useState(false)
  const [emailInput, setEmailInput] = useState('')
  const [emailError, setEmailError] = useState('')
  const [planSteps, setPlanSteps] = useState<PlanStep[]>([])
  const [toolCalls, setToolCalls] = useState<ToolCallEvent[]>([])
  const [evidenceSources, setEvidenceSources] = useState<EvidenceItem[]>([])
  const [ticketData, setTicketData] = useState<TicketInfo | null>(null)
  const [activeInteraction, setActiveInteraction] = useState<InteractionEvent | null>(null)

  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const fullTextRef = useRef('')

  // Rola para o fim sempre que as mensagens ou streaming mudam
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading, streamingText, requiresEmail, planSteps, toolCalls, ticketData, activeInteraction])

  // Cancela streaming ao desmontar
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  // Histórico desabilitado — sessão nova a cada refresh

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return

    // Se o pipeline pediu e-mail e o usuário digitou um e-mail no campo normal,
    // redireciona para o fluxo de captura (evita enviar o e-mail como mensagem)
    if (requiresEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.trim())) {
      setInput('')
      await handleEmailSubmit(text.trim())
      return
    }

    abortRef.current?.abort()
    const abort = new AbortController()
    abortRef.current = abort

    const userMsg: Message = { role: 'user', content: text.trim() }
    const newMessages = [...messages, userMsg]
    setMessages(newMessages)
    setInput('')
    setSuggestions([])
    setIsLoading(true)
    setStreamingText(null)
    setRequiresEmail(false)
    setRelatedArticles([])
    setThinkingSteps([])
    setThinkingCollapsed(false)
    setPlanSteps([])
    setToolCalls([])
    setEvidenceSources([])
    setTicketData(null)
    setActiveInteraction(null)
    fullTextRef.current = ''

    // Histórico sem boas-vindas, sem a mensagem atual
    const history = newMessages.slice(1, -1).map((m) => ({
      role: m.role,
      content: m.content,
    }))

    try {
      await streamSupportMessage(
        { message: text.trim(), history, userEmail: userEmail ?? undefined, sessionId: sessionIdRef.current },
        (event) => {
          if (event.type === 'thinking') {
            setThinkingSteps((prev) => {
              const idx = prev.findIndex((s) => s.step === event.step)
              const updated: ThinkingStep = { step: event.step, text: event.text, status: event.status }
              if (idx >= 0) {
                const n = [...prev]
                n[idx] = updated
                return n
              }
              return [...prev, updated]
            })
          } else if (event.type === 'chunk') {
            fullTextRef.current += event.text
            setStreamingText(fullTextRef.current)
            // Remove TypingIndicator quando chega o primeiro chunk
            setIsLoading(false)
          } else if (event.type === 'requires_email') {
            setIsLoading(false)
            setStreamingText(null)
            setRequiresEmail(true)
          } else if (event.type === 'plan') {
            setPlanSteps(event.steps)
          } else if (event.type === 'tool_call') {
            setToolCalls((prev) => {
              const idx = prev.findIndex((c) => c.tool === event.tool)
              const updated: ToolCallEvent = { tool: event.tool, status: event.status, resultSummary: event.resultSummary }
              if (idx >= 0) {
                const n = [...prev]
                n[idx] = updated
                return n
              }
              return [...prev, updated]
            })
          } else if (event.type === 'evidence') {
            setEvidenceSources(event.sources)
          } else if (event.type === 'clarify') {
            setIsLoading(false)
            setStreamingText(null)
            setMessages((prev) => [...prev, { role: 'assistant', content: event.question }])
            if (event.options?.length) {
              setSuggestions(event.options)
            }
          } else if (event.type === 'ticket_created') {
            setTicketData(event.ticket)
          } else if (event.type === 'interaction') {
            setIsLoading(false)
            setStreamingText(null)
            if (event.message) {
              setMessages((prev) => [...prev, { role: 'assistant', content: event.message! }])
            } else if (event.title) {
              setMessages((prev) => [...prev, { role: 'assistant', content: event.title }])
            }
            setActiveInteraction(event as InteractionEvent)
            setSuggestions([])
          } else if (event.type === 'done') {
            setThinkingCollapsed(true)
            setStreamingText(null)
            if (fullTextRef.current) {
              setMessages((prev) => [
                ...prev,
                { role: 'assistant', content: fullTextRef.current },
              ])
            }
            setSuggestions((prev) => prev.length > 0 ? prev : (event.suggestedReplies ?? []))
            setRelatedArticles(event.relatedArticles ?? [])
            if (event.evidence) setEvidenceSources(event.evidence)
          } else if (event.type === 'error') {
            setIsLoading(false)
            setStreamingText(null)
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content:
                  event.message ||
                  'Ocorreu um erro inesperado. Tente novamente ou entre em contato pelo e-mail suporte@support.ai.',
              },
            ])
          }
        },
        abort.signal,
      )
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente ou entre em contato pelo e-mail suporte@support.ai.',
        },
      ])
    } finally {
      setIsLoading(false)
      setStreamingText(null)
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  const handleEmailSubmit = async (overrideEmail?: string) => {
    const email = (overrideEmail ?? emailInput).trim()
    if (!email) return

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Por favor, informe um e-mail válido.')
      return
    }

    setEmailError('')
    setUserEmail(email)
    // storeEmail(email) — desabilitado: sessão nova a cada refresh
    setRequiresEmail(false)
    setEmailInput('')

    // Reenvia a última mensagem do usuário com o e-mail identificado
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user')
    if (lastUserMsg) {
      const confirmMsg: Message = { role: 'user', content: `Meu e-mail é ${email}` }
      const newMessages = [...messages, confirmMsg]
      setMessages(newMessages)

      abortRef.current?.abort()
      const abort = new AbortController()
      abortRef.current = abort

      setIsLoading(true)
      setStreamingText(null)
      setThinkingSteps([])
      setThinkingCollapsed(false)
      setPlanSteps([])
      setToolCalls([])
      setEvidenceSources([])
      fullTextRef.current = ''

      const history = newMessages.slice(1, -1).map((m) => ({
        role: m.role,
        content: m.content,
      }))

      try {
        await streamSupportMessage(
          { message: lastUserMsg.content, history, userEmail: email, sessionId: sessionIdRef.current },
          (event) => {
            if (event.type === 'thinking') {
              setThinkingSteps((prev) => {
                const idx = prev.findIndex((s) => s.step === event.step)
                const updated: ThinkingStep = { step: event.step, text: event.text, status: event.status }
                if (idx >= 0) { const n = [...prev]; n[idx] = updated; return n }
                return [...prev, updated]
              })
            } else if (event.type === 'chunk') {
              fullTextRef.current += event.text
              setStreamingText(fullTextRef.current)
              setIsLoading(false)
            } else if (event.type === 'plan') {
              setPlanSteps(event.steps)
            } else if (event.type === 'tool_call') {
              setToolCalls((prev) => {
                const idx = prev.findIndex((c) => c.tool === event.tool)
                const updated: ToolCallEvent = { tool: event.tool, status: event.status, resultSummary: event.resultSummary }
                if (idx >= 0) {
                  const n = [...prev]
                  n[idx] = updated
                  return n
                }
                return [...prev, updated]
              })
            } else if (event.type === 'evidence') {
              setEvidenceSources(event.sources)
            } else if (event.type === 'clarify') {
              setIsLoading(false)
              setStreamingText(null)
              setMessages((prev) => [...prev, { role: 'assistant', content: event.question }])
              if (event.options?.length) {
                setSuggestions(event.options)
              }
            } else if (event.type === 'ticket_created') {
              setTicketData(event.ticket)
            } else if (event.type === 'interaction') {
              setIsLoading(false)
              setStreamingText(null)
              if (event.message) {
                setMessages((prev) => [...prev, { role: 'assistant', content: event.message! }])
              } else if (event.title) {
                setMessages((prev) => [...prev, { role: 'assistant', content: event.title }])
              }
              setActiveInteraction(event as InteractionEvent)
              setSuggestions([])
            } else if (event.type === 'done') {
              setThinkingCollapsed(true)
              setStreamingText(null)
              if (fullTextRef.current) {
                setMessages((prev) => [
                  ...prev,
                  { role: 'assistant', content: fullTextRef.current },
                ])
              }
              setSuggestions((prev) => prev.length > 0 ? prev : (event.suggestedReplies ?? []))
              setRelatedArticles(event.relatedArticles ?? [])
              if (event.evidence) setEvidenceSources(event.evidence)
            } else if (event.type === 'error') {
              setIsLoading(false)
              setStreamingText(null)
              setMessages((prev) => [
                ...prev,
                { role: 'assistant', content: 'Ocorreu um erro. Tente novamente.' },
              ])
            }
          },
          abort.signal,
        )
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'Ocorreu um erro. Tente novamente.' },
        ])
      } finally {
        setIsLoading(false)
        setStreamingText(null)
      }
    }
  }

  const handleEmailKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleEmailSubmit()
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border/50 bg-card/80 backdrop-blur-sm">
        <div className="h-9 w-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
          <span className="text-primary text-base font-bold leading-none">C</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground leading-none">Central de ajuda</p>
          <p className="text-xs text-muted-foreground mt-0.5">Suporte ao aluno</p>
        </div>
        <Badge
          variant="outline"
          className="shrink-0 text-xs border-emerald-500/40 text-emerald-500 bg-emerald-500/10"
        >
          <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />
          Online
        </Badge>
      </div>

      {/* Messages area */}
      <ScrollArea className="flex-1 px-4 py-4">
        <div className="flex flex-col gap-4">
          {messages.map((msg, idx) => (
            <ChatMessage key={idx} role={msg.role} content={msg.content} />
          ))}

          {/* Thinking steps — exibidos enquanto o pipeline processa */}
          <ThinkingSteps steps={thinkingSteps} collapsed={thinkingCollapsed} />

          {/* Plan view — plano de execução do orquestrador */}
          <PlanView steps={planSteps} />

          {/* Tool call indicators — ferramentas sendo executadas */}
          <ToolCallIndicator calls={toolCalls} />

          {/* Ticket card — ticket criado no Octadesk */}
          {ticketData && <TicketCard ticket={ticketData} />}

          {/* Interaction card — enquete / confirmação / input estruturado */}
          {activeInteraction && !isLoading && (
            <InteractionCard
              interaction={activeInteraction}
              onRespond={(value) => {
                setActiveInteraction(null)
                sendMessage(value)
              }}
            />
          )}

          {/* Typing indicator (antes do primeiro chunk) */}
          <AnimatePresence>
            {isLoading && (
              <motion.div
                key="typing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <TypingIndicator />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mensagem sendo construída em streaming */}
          <AnimatePresence>
            {streamingText !== null && (
              <motion.div
                key="streaming"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <ChatMessage role="assistant" content={streamingText} isStreaming />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Quick reply chips */}
          {!isLoading && streamingText === null && suggestions.length > 0 && (
            <QuickReplies
              replies={suggestions}
              onSelect={(reply) => sendMessage(reply)}
            />
          )}

          {/* Artigos relacionados */}
          {!isLoading && streamingText === null && relatedArticles.length > 0 && (
            <div className="ml-9 space-y-1.5">
              <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide">
                Artigos relacionados
              </p>
              {relatedArticles.map((article) => {
                const href = article.sourceUrl ?? `/artigo/${article.id}`
                return (
                  <a
                    key={article.id}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs text-foreground hover:bg-accent transition-colors"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="truncate">{article.title}</span>
                  </a>
                )
              })}
            </div>
          )}

          {/* Evidence panel — fontes consultadas */}
          {!isLoading && streamingText === null && evidenceSources.length > 0 && (
            <EvidencePanel sources={evidenceSources} />
          )}

          {/* Email collection widget */}
          <AnimatePresence>
            {requiresEmail && !isLoading && (
              <motion.div
                key="email-widget"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="ml-9 flex flex-col gap-2"
              >
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value)
                        setEmailError('')
                      }}
                      onKeyDown={handleEmailKeyDown}
                      className="pl-9 h-9 text-sm"
                      autoFocus
                    />
                  </div>
                  <Button size="sm" className="h-9 px-4" onClick={() => handleEmailSubmit()}>
                    Confirmar
                  </Button>
                </div>
                {emailError && (
                  <p className="text-xs text-destructive">{emailError}</p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      {/* Input area */}
      <div className="px-4 py-3 border-t border-border/50 bg-card/80 backdrop-blur-sm">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Digite sua mensagem..."
            disabled={isLoading || streamingText !== null}
            className="flex-1 h-10 text-sm bg-background/60"
          />
          <Button
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || isLoading || streamingText !== null}
          >
            <SendIcon className="h-4 w-4" />
          </Button>
        </div>
        <p className="mt-2 text-center text-[11px] text-muted-foreground/60">
          Central de ajuda · suporte@support.ai
        </p>
      </div>
    </div>
  )
}
